import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Card } from '@/components/ui'
import { TabBar } from '@/components/TabBar'
import { AgentProviderTokenSection } from '@/components/AgentProviderTokenSection'
import { BillingProviderSection } from '@/components/BillingProviderSection'
import { AzureDevOpsIngestionSection, PackageFeedsSection } from '@/components/projects/settings'
import { ProjectSectionHeader } from '@/components/projects'
import { useAuth } from '@/hooks/useAuth'
import { Permissions } from '@/lib/permissions'
import {
  useProjectClaudeApiKey,
  useSaveProjectClaudeApiKey,
  useDeleteProjectClaudeApiKey,
  useTenantClaudeApiKey,
  useUserClaudeToken,
  useProjectCopilotApiKey,
  useSaveProjectCopilotApiKey,
  useDeleteProjectCopilotApiKey,
  useTenantCopilotApiKey,
  useConnectors,
  useProjectClaudeSpendingCap,
  useSetProjectClaudeSpendingCap,
  useProjectCopilotSpendingCap,
  useSetProjectCopilotSpendingCap,
} from '@/hooks'
import { useTokenAnalyticsByWorkflow } from '@/hooks/useAnalytics'

type SettingsTab = 'billing' | 'api-keys' | 'package-feeds' | 'azure-devops-ingestion'

const SETTINGS_TABS: { key: SettingsTab; label: string; tag?: string }[] = [
  { key: 'api-keys', label: 'API Keys' },
  { key: 'package-feeds', label: 'Package Feeds' },
  { key: 'billing', label: 'Billing & Limits' },
  { key: 'azure-devops-ingestion', label: 'Azure DevOps Ingestion' },
]

const isSettingsTab = (value: string | null): value is SettingsTab => SETTINGS_TABS.some(tab => tab.key === value)

export function ProjectSettingsPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const [searchParams] = useSearchParams()
  const { hasPermission, isAdmin, isLoading: isAuthLoading } = useAuth(projectId)
  const requestedTab = searchParams.get('tab')
  const [activeTab, setActiveTab] = useState<SettingsTab>(isSettingsTab(requestedTab) ? requestedTab : 'api-keys')

  const canManage =
    isAdmin || hasPermission(Permissions.Project.Settings.Manage, projectId)
  const canManageFeeds =
    isAdmin || hasPermission(Permissions.Project.Feeds.Manage, projectId)
  const canManageTenantSettings =
    isAdmin || hasPermission(Permissions.Host.Tenant.Settings.Manage)
  const canManageUserToken = hasPermission(Permissions.Host.User.Settings.ManageClaudeToken)
  const { data: connectors = [] } = useConnectors(projectId ?? '')

  const { data: projectKey, isLoading: isProjectLoading } = useProjectClaudeApiKey(projectId, canManage)
  const { data: tenantKey } = useTenantClaudeApiKey(canManage)
  const { data: userToken } = useUserClaudeToken(canManage && canManageUserToken)

  const saveKey = useSaveProjectClaudeApiKey(projectId)
  const deleteKey = useDeleteProjectClaudeApiKey(projectId)

  const { data: copilotProjectKey, isLoading: isCopilotProjectLoading } = useProjectCopilotApiKey(projectId, canManage)
  const { data: copilotTenantKey } = useTenantCopilotApiKey(canManage)
  const saveCopilotKey = useSaveProjectCopilotApiKey(projectId)
  const deleteCopilotKey = useDeleteProjectCopilotApiKey(projectId)

  const { data: claudeSpendingCap, isLoading: isClaudeSpendingCapLoading } = useProjectClaudeSpendingCap(projectId, canManage)
  const setClaudeSpendingCap = useSetProjectClaudeSpendingCap(projectId)

  const { data: copilotSpendingCap, isLoading: isCopilotSpendingCapLoading } = useProjectCopilotSpendingCap(projectId, canManage)
  const setCopilotSpendingCap = useSetProjectCopilotSpendingCap(projectId)

  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  const localDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
  const currentMonth = {
    from: localDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: localDate(now),
  }
  const { data: currentMonthClaudeUsage } = useTokenAnalyticsByWorkflow(projectId, { provider: 'Anthropic', ...currentMonth })
  const { data: currentMonthCopilotUsage } = useTokenAnalyticsByWorkflow(projectId, { provider: 'GitHub', ...currentMonth })

  if (!projectId) {
    return <Navigate to="/projects" replace />
  }

  if (isAuthLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading" />
      </div>
    )
  }

  if (!canManage) {
    return <Navigate to={`/projects/${projectId}`} replace />
  }

  const projectHasKey = projectKey?.hasKey ?? false
  const tenantHasKey = tenantKey?.hasKey ?? false
  const userHasToken = userToken?.hasToken ?? false
  const userHasAnthropicConnector = connectors.some(c => c.type === 'Anthropic')
  const userHasCopilotConnector = connectors.some(c => c.type === 'GitHubCopilot')

  let fallbackInfo: React.ReactNode = null
  if (userHasToken) {
    fallbackInfo = (
      <p
        role="status"
        aria-live="polite"
        className="rounded border border-border bg-muted px-4 py-3 text-sm"
        data-testid="project-claude-api-key-using-user-token-info"
      >
        Currently using your Claude OAuth token. It is used before any Anthropic API key; other users on this project may need their own key, a project key or a tenant key.
      </p>
    )
  } else if (userHasAnthropicConnector) {
    fallbackInfo = (
      <p
        role="status"
        aria-live="polite"
        className="rounded border border-border bg-muted px-4 py-3 text-sm"
        data-testid="project-claude-api-key-using-connector-info"
      >
        Currently using your Anthropic key from this project's{' '}
        <Link to={`/projects/${projectId}/connectors`} className="underline hover:text-foreground">Connectors</Link> page.
      </p>
    )
  } else if (!projectHasKey && tenantHasKey) {
    fallbackInfo = (
      <p
        role="status"
        aria-live="polite"
        className="rounded border border-border bg-muted px-4 py-3 text-sm"
        data-testid="project-claude-api-key-using-tenant-info"
      >
        Currently using the tenant-level API key.
        {canManageTenantSettings && (
          <>
            {' '}
            <Link to="/tenant/settings" className="underline hover:text-foreground">
              Manage tenant key →
            </Link>
          </>
        )}
      </p>
    )
  } else if (!projectHasKey && !tenantHasKey) {
    fallbackInfo = (
      <p
        role="alert"
        className="rounded border border-danger-foreground bg-danger px-4 py-3 text-sm text-danger-foreground"
        data-testid="project-claude-api-key-missing-warning"
      >
        No Claude credential is configured for this project or its tenant. Workflows and chats in this project will fail until you configure a key here, connect Anthropic on the Connectors page, set a Claude OAuth token in your user settings, or {canManageTenantSettings ? (
          <>
            configure one in{' '}
            <Link to="/tenant/settings" className="underline hover:text-destructive">
              Tenant Settings
            </Link>
          </>
        ) : (
          'an administrator configures one in Tenant Settings'
        )}.
      </p>
    )
  }

  const copilotProjectHasKey = copilotProjectKey?.hasKey ?? false
  const copilotTenantHasKey = copilotTenantKey?.hasKey ?? false

  let copilotFallbackInfo: React.ReactNode = null
  if (userHasCopilotConnector) {
    copilotFallbackInfo = (
      <p
        role="status"
        aria-live="polite"
        className="rounded border border-border bg-muted px-4 py-3 text-sm"
        data-testid="project-copilot-api-key-using-connector-info"
      >
        Currently using your GitHub Copilot token from this project's{' '}
        <Link to={`/projects/${projectId}/connectors`} className="underline hover:text-foreground">Connectors</Link> page. Other users on this project may need their own token, a project token or a tenant token.
      </p>
    )
  } else if (!copilotProjectHasKey && copilotTenantHasKey) {
    copilotFallbackInfo = (
      <p
        role="status"
        aria-live="polite"
        className="rounded border border-border bg-muted px-4 py-3 text-sm"
        data-testid="project-copilot-api-key-using-tenant-info"
      >
        Currently using the tenant-level GitHub token.
        {canManageTenantSettings && (
          <>
            {' '}
            <Link to="/tenant/settings" className="underline hover:text-foreground">
              Manage tenant token →
            </Link>
          </>
        )}
      </p>
    )
  } else if (!copilotProjectHasKey && !copilotTenantHasKey) {
    copilotFallbackInfo = (
      <p
        role="alert"
        className="rounded border border-danger-foreground bg-danger px-4 py-3 text-sm text-danger-foreground"
        data-testid="project-copilot-api-key-missing-warning"
      >
        No Copilot credential is configured for this project or its tenant. Copilot CLI workflows in this project will fail until you configure a token here, connect GitHub Copilot on the Connectors page, or {canManageTenantSettings ? (
          <>
            configure one in{' '}
            <Link to="/tenant/settings" className="underline hover:text-destructive">
              Tenant Settings
            </Link>
          </>
        ) : (
          'an administrator configures one in Tenant Settings'
        )}.
      </p>
    )
  }

  return (
    <div className="space-y-5" aria-label="Project settings">
      <ProjectSectionHeader sectionName="Settings" />

      <TabBar
        tabs={SETTINGS_TABS}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        className="w-fit"
      />

      {activeTab === 'azure-devops-ingestion' && (
        <AzureDevOpsIngestionSection projectId={projectId} />
      )}

      {activeTab === 'billing' && (
        <Card className="p-6">
          <BillingProviderSection
            hasActiveCopilot={copilotProjectHasKey || userHasCopilotConnector}
            isLoadingProviderState={isCopilotProjectLoading}
            readOnly={!isAdmin}
            claudeBilling={{
              capUsd: claudeSpendingCap?.capUsd,
              currentSpendUsd: currentMonthClaudeUsage?.totalCostUsd,
              isLoading: isClaudeSpendingCapLoading,
              isSaving: setClaudeSpendingCap.isPending,
              onSave: async (capUsd: number | null) => {
                await setClaudeSpendingCap.mutateAsync(capUsd)
                toast.success('Spending cap saved.')
              },
            }}
            copilotBilling={{
              capUsd: copilotSpendingCap?.capUsd,
              currentSpendUsd: currentMonthCopilotUsage?.totalCostUsd,
              isLoading: isCopilotSpendingCapLoading,
              isSaving: setCopilotSpendingCap.isPending,
              onSave: async (capUsd: number | null) => {
                await setCopilotSpendingCap.mutateAsync(capUsd)
                toast.success('Spending cap saved.')
              },
            }}
          />
        </Card>
      )}

      {activeTab === 'api-keys' && (
        <Card className="p-6">
          <AgentProviderTokenSection
            claude={{
              title: 'Claude Code API Key',
              description: 'Project-specific Anthropic API key. When set, it overrides the tenant-level key for workflows and chats in this project. A user\'s Claude OAuth token, then their own Anthropic key on the Connectors page, take precedence over both.',
              hasKey: projectHasKey,
              isLoading: isProjectLoading,
              isSaving: saveKey.isPending,
              isDeleting: deleteKey.isPending,
              onSave: async (apiKey) => {
                await saveKey.mutateAsync(apiKey)
                toast.success('Project Claude API key saved.')
              },
              onDelete: async () => {
                await deleteKey.mutateAsync()
                toast.success('Project Claude API key removed.')
              },
              fallbackInfo: fallbackInfo,
              testIdPrefix: 'project-claude-api-key',
            }}
            copilot={{
              title: 'Copilot CLI GitHub Token',
              description: 'Project-specific GitHub token for Copilot CLI. When set, it overrides the tenant-level token for Copilot CLI workflows in this project. Each user\'s own GitHub Copilot token on the Connectors page takes precedence over both project and tenant tokens.',
              hasKey: copilotProjectHasKey,
              isLoading: isCopilotProjectLoading,
              isSaving: saveCopilotKey.isPending,
              isDeleting: deleteCopilotKey.isPending,
              onSave: async (apiKey) => {
                await saveCopilotKey.mutateAsync(apiKey)
                toast.success('Project Copilot CLI token saved.')
              },
              onDelete: async () => {
                await deleteCopilotKey.mutateAsync()
                toast.success('Project Copilot CLI token removed.')
              },
              fallbackInfo: copilotFallbackInfo,
              testIdPrefix: 'project-copilot-api-key',
            }}
            testIdPrefix="project-ai-provider"
          />
        </Card>
      )}

      {activeTab === 'package-feeds' && (
        <PackageFeedsSection projectId={projectId} canManage={canManageFeeds} />
      )}
    </div>
  )
}
